import { ref, computed } from 'vue'
import { cloneDeep, head, get } from 'lodash'
import { DateOption, ReportFilters } from '../types/report-filters'
import { BBTreeviewNode, CheckboxNode, DateNode, LocationFilters, Panel } from '../types'

export function useSearchFilters() {
  const searchPhrase = ref('')

  const searchRegex = computed(() => new RegExp(`${searchPhrase.value}`, 'gi'))

  function setSearchPhrase(searchTerm: string): any {
    searchPhrase.value = searchTerm
  }

  function searchProductFilters(locationFilters: LocationFilters[]): LocationFilters[] {
    const clonedFilters = cloneDeep(locationFilters)
    // get the existing state from ls
    let filterData: ReportFilters = {}
    try {
      const filters = localStorage.getItem('filter-panel-filters')

      if (filters) {
        filterData = JSON.parse(filters)
      }
    } catch (err) {
      console.warn('Error reading filter-panel-filters from local storage ', err)
    }

    return clonedFilters.filter(filter => {
      if (filter.type === 'nested') {
        filter.panels = filter.panels
          .map((cfDividerPanel: Panel) => {
            const panelIncludesSearchPhrase = cfDividerPanel.name.match(searchRegex.value)
            if (!cfDividerPanel.children && !panelIncludesSearchPhrase) return
            if (cfDividerPanel.name !== 'Taxonomy') {
              // hydrate the node values from local storage
              cfDividerPanel.children = mapDividerPanels(cfDividerPanel, filterData)
            }
            // If the search matches the CF Divider name return the whole panel and children
            if (panelIncludesSearchPhrase) return cfDividerPanel
            // Filter out top level taxonomy/custom field panels that do not match
            cfDividerPanel.children = filterDividerPanels(cfDividerPanel)
            // if children are present we have matches to the search and want to return the cfDividerPanel
            if (cfDividerPanel.children.length) return cfDividerPanel
          })
          .filter(p => !!p) as Panel[]

        return filter.panels.length
      }
      filter.options = filter.options.map(option => {
        const valueFromStorage = filterData[option.groupingKey as keyof ReportFilters]
        return {
          ...option,
          selected: valueFromStorage ? valueFromStorage : false
        } as Pick<CheckboxNode, 'name' | 'id' | 'groupingKey' | 'selected'>
      })
      return filter.options.some(opt => opt.name.match(searchRegex.value))
    })
  }

  function filterTreeView(item: any, search: string, textKey: string) {
    const regex = new RegExp(`${search}`, 'gi')
    const keys = ['all_brands', 'all_years', 'all_seasons', 'all_divisions', 'all_categories']
    /**
     * this specifically handles searching for the expansion panel "Taxonomy".
     * When searching "Taxonomy", we need to show all items in the treeview
     */
    if ('taxonomy'.includes(search.toLowerCase())) return true
    if (keys.includes(item.id)) return regex.test(item[textKey])

    return false
  }

  function mapDividerPanels(cfDividerPanel: Panel, filterData: ReportFilters) {
    const { custom_fields } = filterData
    const customFieldIds = custom_fields?.map(head).filter(Boolean) || []
    return cfDividerPanel.children.map((child: BBTreeviewNode) => {
      const [_, id] = child.id.split('.')
      const getRecord = (cfId: string) => custom_fields?.find((cf: any) => head(cf) === cfId)
      if (id && !customFieldIds.includes(id)) return child
      if (id && customFieldIds.includes(id)) {
        if (child.type === 'Checkbox') {
          return {
            ...child,
            children: child.children?.map((ch: BBTreeviewNode) => {
              if (ch.type === 'Checkbox') {
                const record = getRecord(id)
                const values = get(record, 1, []) as Array<string | boolean | null>
                const selected = values.length && ch.value !== undefined ? values.includes(ch.value) : false
                return {
                  ...ch,
                  selected
                }
              }
              return ch
            })
          } as CheckboxNode
        }
        // else we know it's a Date type (TS does too)
        return {
          ...child,
          children: child.children?.map(ch => {
            const record = getRecord(id)
            const value = get(record, 1, {}) as DateOption
            return {
              ...ch,
              value: {
                start: value.start || '',
                end: value.end || ''
              }
            }
          })
        } as DateNode
      }
      return child
    })
  }

  function filterDividerPanels(cfDividerPanel: Panel) {
    return cfDividerPanel.children.filter((child: BBTreeviewNode) => {
      const titleIncludesSearchPhrase = child.name.match(searchRegex.value)
      return titleIncludesSearchPhrase
    })
  }

  return {
    // data
    searchPhrase,
    // apis
    filterTreeView,
    setSearchPhrase,
    searchProductFilters
  }
}
